import { html, LitElement, TemplateResult } from 'lit';
import {property, state} from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { filter, Subject, takeUntil } from 'rxjs';
import {
  DynamicTreeDataSource,
  DynamicTreeDataSourceOptions,
  NodeData,
  SelectedNodes,
  StaticTreeDataSource,
  StaticTreeDataSourceOptions,
  TreeDataSource,
  TreeDataSourceType,
  TreeEvent,
} from '../type';
import { styles } from '../view/styles';
import { classMap } from 'lit/directives/class-map.js';
import ScrollToTargetController from "../../reactiveControllers/ScrollToTargetController";

export class GzTreeWebComponent<T extends object> extends LitElement {
  @property({ type: Object })
  public dataSource: TreeDataSource;

  @property({ type: Function })
  public nodeRenderer: Function;

  @property({ type: Boolean })
  public initialLoading = true;

  @state()
  private nodes: Map<string, NodeData>;

  /**
   *  This is a shared registry of the flattened elements inside the tree.
   *  It's used for quick access to the nodes html elements, so we don't have to query the DOM every time we need to access a node.
   * @private
   */
  private nodeRefs: Map<string, HTMLElement> = new Map();

  @state()
  private loading = false;

  /* Property that provides an array with selection of the nodes */
  @property({ type: Array, attribute: true, reflect: true })
  selectedNodes: SelectedNodes<T>[] = [];

  @property({ type: Object, attribute: true, reflect: true })
  translations: { loadMoreNodes: string } = {
    loadMoreNodes: 'LOAD_MORE_NODES_PLACEHOLDER',
  };

  static get styles() {
    return styles;
  }

  private unsubscribe$ = new Subject();

  private scrollToTargetController: ScrollToTargetController;

  constructor() {
    super();
    this.scrollToTargetController = new ScrollToTargetController(this, this.parentElement as HTMLElement);
  }

  static buildTreeElement(
    wrapper: HTMLElement,
    options: DynamicTreeDataSourceOptions | StaticTreeDataSourceOptions,
    dataSourceType: TreeDataSourceType
  ): GzTreeWebComponent<any> {
    const dataSource =
      dataSourceType === TreeDataSourceType.DYNAMIC
        ? new DynamicTreeDataSource(options as DynamicTreeDataSourceOptions)
        : new StaticTreeDataSource(options as StaticTreeDataSourceOptions);

    const tree = document.createElement('gz-tree') as GzTreeWebComponent<any>;
    tree.dataSource = dataSource;
    wrapper.appendChild(tree);
    return tree;
  }

  private getSelectedOptions(array: SelectedNodes<T>[], value: { key: string; value: T }) {
    for (let i = 0; i < array.length; i++) {
      const item = array[i];
      if (item.key === value.key) {
        array = array.filter(item => item.key !== value.key);

        return array;
      }
    }

    array.push(value);

    return array;
  }

  connectedCallback(): void {
    super.connectedCallback();

    this.dataSource.nodes$
      .pipe(
        filter((nodes: Map<string, NodeData>) => !!nodes.size),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(nodes => {
        this.nodes = nodes;
        this.initialLoading = false;
      });

    this.addEventListener(TreeEvent.NODE_CHECKED, this.handleCheckedNodes);

    this.dataSource.loading$.pipe(takeUntil(this.unsubscribe$)).subscribe(loading => (this.loading = loading));
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener(TreeEvent.NODE_CHECKED, this.handleCheckedNodes);
    this.unsubscribe$.next(true);
  }

  private handleCheckedNodes(e: CustomEvent) {
    this.itemChange(e.detail);

    this.recursiveParentIteration(e.detail.value.parentNodeId);
    this.recursiveChildIteration(e.detail.value.childNodeIds);
  }

  private recursiveParentIteration(nodeId: string) {
    let grandParent;

    if (!nodeId) {
      return;
    }

    for (let i = 0; i < this.selectedNodes.length; i++) {
      const item = this.selectedNodes[i];
      if (item.key === nodeId) {
        this.selectedNodes = this.selectedNodes.filter(item => item.key !== nodeId);
      }
    }

    for (const [key, item] of this.nodes) {
      if (item.nodeId === nodeId) {
        grandParent = item.parentNodeId;

        if (grandParent) {
          this.recursiveParentIteration(grandParent);
        }
      }
    }
  }

  private recursiveChildIteration(nodeIds: Array<string>) {
    let grandChild;

    if (!nodeIds) {
      return;
    }

    const selectedNodeKeys = this.selectedNodes.map(node => node.key);

    for (let i = 0; i < nodeIds.length; i++) {
      if (selectedNodeKeys.includes(nodeIds[i])) {
        this.selectedNodes = this.selectedNodes.filter(node => node.key !== nodeIds[i]);
      }

      const item = this.nodes.get(nodeIds[i]);
      if (item) {
        grandChild = item.childNodeIds;

        if (grandChild) {
          this.recursiveChildIteration(grandChild);
        }
      }
    }
  }

  private renderInitialLoader(): TemplateResult {
    return html`<gz-loader class="tree-loader" rows="8"></gz-loader>`;
  }

  private renderSpinner(): TemplateResult {
    return html`<gz-progress-spinner class="spinner" spinnerWidth="large"></gz-progress-spinner>`;
  }

  private itemChange(node: SelectedNodes<T>) {
    if (this.dataSource.options?.singleSelect) {
      //on single select, ensure that only one item is selected and the item selected is different than the one already checked
      this.selectedNodes =
        (this.selectedNodes.length && this.selectedNodes[0].key !== node.key) || this.selectedNodes.length === 0
          ? [node]
          : [];
    } else {
      const selected = this.getSelectedOptions(this.selectedNodes, node);
      this.selectedNodes = [...selected];
    }
  }

  private getNodeHtmlElement(nodeId: string): HTMLElement | null {
    if (!this.nodeRefs.has(nodeId)) {
      return null;
    }
    return this.nodeRefs.get(nodeId) as HTMLElement;
  }

  /**
   * This method is used to scroll to a specific node in the tree.
   * @param nodeId
   */
  public scrollToNode(nodeId: string | null): void {
    if (!nodeId) {
      return;
    }

    setTimeout(() => {
      const node = this.getNodeHtmlElement(nodeId) as HTMLElement;
      const nodeLabel = node.shadowRoot?.querySelector('.node--active') as HTMLElement;
      const targetElement = nodeLabel || node;

      this.scrollToTargetController.scrollToTarget(targetElement, {
        block: 'center',
        inline: 'center',
      });
    }, 0);
  }


  render() {
    if (this.initialLoading) {
      return this.renderInitialLoader();
    }

    const classes = classMap({
      'node-container--loading': this.loading,
    });

    return html`
      <section class=${classes}>
        ${repeat(
          this.dataSource.options.rootNodeIds,
          id => id,
          (id: string, index: number) => {
            return html`
            <gz-node-director
              .nodeRefs=${this.nodeRefs}
              data-level="${index}"
              .nodeId=${id}
              .dataSource=${this.dataSource}
              .nodeRenderer=${this.nodeRenderer}
              .node=${this.nodes.get(id)}
              .nodes=${this.nodes}
              .selectedNodes="${this.selectedNodes}"
              .depth=${0}
              .translations=${this.translations}
            ></gz-node-director>
          `
          }
        )}
      </section>
      ${this.loading ? this.renderSpinner() : ''}
    `;
  }

  get treeNodesMap(): Map<string, NodeData> {
    return this.nodes;
  }
}
